/* VIEW — PAGES. Exact port of code.txt PagesView (list + page detail). */

import { Card, Lbl, SectionTitle, Pill, SearchBar, ChipRow, ExportBar, Pager, wirePager, TH, TD, StatusPill, TIER_STYLE, SEARCH_LABEL_C, SEARCH_LABEL_BG, T, MONO, fmt, escapeHtml, OpenLink, wireOpenLinks} from "../../ui.js";
import { searchLabel, aiLabel, pathOf } from "@agf/analyzer";
import { KeywordTable, MiniStat, BackBar } from "./widgets.js";
import { AioLabel } from "../../ui.js";

export function renderPages(el, { R, selected, setSelected, openTopic }) {
  /* ----- detail ----- */
  if (selected) {
    const p = R.pageByUrl.get(selected);
    if (!p) { setSelected(null); return; }
    const pageTopics = R.topics.filter(t => t.page === p.url).sort((a, b) => b.imp - a.imp);
    /* Topics that live on OTHER pages but sit closest in meaning to this one — shown when
       the page owns none, so a high-traffic page never presents an unexplained blank. */
    const near = (!pageTopics.length && p.nearTopics ? p.nearTopics : [])
      .map(n => ({ ...n, t: R.topics.find(t => t.id === n.id) })).filter(n => n.t);
    el.innerHTML = `
      ${BackBar("All pages", 'id="pages-back"')}
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:16px">
        <h1 style="font-size:20px;font-weight:800;letter-spacing:-0.4px;margin:0;font-family:${MONO}">${escapeHtml(p.url)}</h1>${OpenLink(p.url, 15)}
        ${StatusPill(p.status)}
      </div>
      <div style="display:grid;grid-template-columns:minmax(0,1.3fr) minmax(0,1fr);gap:12px;margin-bottom:16px">
        ${Card(`
          ${SectionTitle(`<span style="display:inline-flex;align-items:center;gap:7px"><span style="width:18px;height:18px;border-radius:6px;background:${T.violetSoft};color:${T.violet};font-family:${MONO};font-weight:800;font-size:11px;display:inline-flex;align-items:center;justify-content:center">1</span>Google Search performance</span>`, { right: p.searchScore == null ? "not in your top search queries" : null })}
          <div style="display:grid;grid-template-columns:repeat(3, minmax(0,1fr));gap:8px">
            ${MiniStat("Strength", p.searchScore == null ? "\u2014" : searchLabel(p.searchScore))}
            ${MiniStat("Impressions", fmt(p.trad.imp))}
            ${MiniStat("Clicks", fmt(p.trad.clicks))}
            ${MiniStat("CTR", p.trad.imp ? (100 * p.trad.clicks / p.trad.imp).toFixed(1) + "%" : "\u2014")}
            ${MiniStat("Avg position", p.pos ? p.pos.toFixed(1) : "\u2014")}
          </div>`,
        { style: `padding:16px 18px;border-top:3px solid ${T.violet}` })}
        ${Card(`
          ${SectionTitle(`<span style="display:inline-flex;align-items:center;gap:7px"><span style="width:18px;height:18px;border-radius:6px;background:${T.magentaSoft};color:${T.magenta};font-family:${MONO};font-weight:800;font-size:11px;display:inline-flex;align-items:center;justify-content:center">2</span>${AioLabel("Generative AI", 11)}</span>`)}
          <div style="display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:8px">
            ${MiniStat("AI visibility", p.aiScore == null ? (p.ai.imp > 0 ? "Strong" : "\u2014") : aiLabel(p.aiScore), { ai: true })}
            ${MiniStat("AI impressions", fmt(p.ai.imp), { ai: true, sub: p.aiIdx == null ? `${(p.aiShare * 100).toFixed(1)}% of site AI` : `~${p.aiIdx.toFixed(2)}× expected (est.)` })}
            ${R.meta.hasSerp ? MiniStat("AIO keyword clicks", p.aioClicks == null ? "\u2014" : fmt(p.aioClicks), { ai: true }) : ""}
            ${R.meta.hasSerp ? MiniStat("AIO keyword impr.", p.aioImp == null ? "\u2014" : fmt(p.aioImp), { ai: true }) : ""}
          </div>`,
        { ai: true, style: `padding:16px 18px;border-top:3px solid ${T.magenta}` })}
      </div>
      ${KeywordTable({
        title: "Keywords this page ranks for",
        queries: p.topics.flatMap(t => t.queries),
        hasSerp: R.meta.hasSerp,
        exportBase: "page-keywords-" + p.path.replace(/[^a-z0-9]+/gi, "-"),
        note: p.topics.length
          ? "Keywords are connected to this page by matching topics to the URL — Google doesn't provide a direct keyword-to-page mapping for AI data, so treat the linkage as inferred."
          : "Google's exports list your searches and your pages separately and never say which search belongs to which page, so we connect each topic to the page closest to it in meaning. No topic was close enough to this page to connect honestly — the impressions and clicks above are still exact.",
      })}
      ${Card(`
        ${SectionTitle("Topics this page performs for")}
        ${pageTopics.length === 0 ? `<div style="font-size:12.5px;color:${T.sub}">No topic could be tied to this URL${near.length ? " — the closest ones below are attached to a sibling page that sits nearer in meaning" : ". Every topic sits closer in meaning to some other page — rather than guess, we leave the page unconnected"}.</div>` : ""}
        ${pageTopics.map(t => {
          const sl = searchLabel(t.searchScore), al = aiLabel(t.aiScore);
          const gapBad = t.searchScore >= 55 && t.aiScore < 45;
          return `
            <div data-open-topic="${t.id}" class="click-card" style="display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr) auto;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid ${T.lineSoft};cursor:pointer">
              <div style="font-weight:700;font-size:13px">${escapeHtml(t.name)}</div>
              <div style="font-size:12px;font-weight:700;color:${SEARCH_LABEL_C[sl]}">${sl} Search</div>
              <div style="font-size:12px;font-weight:700;color:${T.magenta}">${al} AI</div>
              ${gapBad ? Pill(T.amber, T.amberSoft, "Gap") : Pill(T.green, T.greenSoft, "OK")}
            </div>`;
        }).join("")}
        ${near.length ? `
          <div style="margin-top:14px">
            ${Lbl("Closest topics — attached elsewhere")}
            ${near.map(n => `
              <div data-open-topic="${n.t.id}" class="click-card" style="display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,1fr) auto;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid ${T.lineSoft};cursor:pointer">
                <div style="font-weight:700;font-size:13px">${escapeHtml(n.t.name)} <span style="font-weight:500;color:${T.muted};font-size:11.5px">${Math.round(n.sim * 100)}% close</span></div>
                <div style="font-family:${MONO};font-size:11.5px;color:${T.sub};overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${n.t.page ? "→ " + escapeHtml(pathOf(n.t.page)) : "not attached to any page"}</div>
                ${Pill(T.violet, T.violetSoft, "View topic")}
              </div>`).join("")}
            <div style="font-size:11.5px;color:${T.muted};margin-top:8px">These searches are about this page's subject, but each topic attaches to the single page nearest to it in meaning — shown here so the traffic above has context. Display only; no score uses this.</div>
          </div>` : ""}`,
      {})}`;

    el.querySelector("#pages-back").addEventListener("click", () => setSelected(null));
    wireOpenLinks(el);
    el.querySelectorAll("[data-open-topic]").forEach(b => b.addEventListener("click", () => openTopic(b.dataset.openTopic)));
    return;
  }

  /* ----- list ----- */
  const st = { search: "", status: "All", page: 0 };
  const PAGE_SIZE = 100;
  /* Typing re-renders the list; debounce keeps it smooth. NOTE: this must be defined in
     THIS view — the search box was dead for a while because the handler referenced a
     helper that only existed in the opportunities view (a ReferenceError per keystroke). */
  let searchTimer = null;
  const debouncedDraw = focus => { clearTimeout(searchTimer); searchTimer = setTimeout(() => draw(focus), 130); };
  const draw = (focusSearch = false) => {
    /* Every matching page is reachable: the full filtered list feeds the export and the
       pager; only the current 100 render, which keeps the DOM fast on 1000+ page sites. */
    const filtered = R.pages.filter(p =>
      (p.trad.imp > 0 || p.ai.imp > 0) &&
      (st.status === "All" || p.status === st.status) &&
      (!st.search || p.url.toLowerCase().includes(st.search.toLowerCase()))
    );
    st.page = Math.min(st.page, Math.max(0, Math.ceil(filtered.length / PAGE_SIZE) - 1));
    const rows = filtered.slice(st.page * PAGE_SIZE, (st.page + 1) * PAGE_SIZE);
    const exportData = filtered.map(p => ({ page: p.url, status: p.status, google_search: p.searchScore == null ? "not in top queries" : searchLabel(p.searchScore), search_score: p.searchScore == null ? "" : p.searchScore, ai_visibility: p.aiScore == null ? "" : aiLabel(p.aiScore), ai_score: p.aiScore == null ? "" : p.aiScore, impressions: p.trad.imp, clicks: p.trad.clicks, ai_impressions: p.ai.imp, ai_share_pct: +(p.aiShare * 100).toFixed(2), aio_keyword_clicks: p.aioClicks == null ? "" : p.aioClicks, aio_keyword_impressions: p.aioImp == null ? "" : p.aioImp, avg_position: p.pos ? +p.pos.toFixed(1) : "", ai_vs_expected: p.aiIdx == null ? "" : +p.aiIdx.toFixed(2) }));
    el.innerHTML = `
      <div style="margin-bottom:16px">
        <div style="font-size:15px;font-weight:800;margin-bottom:3px">Pages</div>
        <div style="font-size:12.5px;color:${T.sub}">Every page, with its Google Search strength and AI visibility side by side.</div>
      </div>
      <div style="display:flex;gap:10px;align-items:center;margin-bottom:14px;flex-wrap:wrap">
        ${SearchBar({ value: st.search, placeholder: "Search pages…", id: "pages-search" })}
        ${ChipRow(["All", "AI Opportunity", "AI Winner", "Healthy"], st.status, "status")}
        ${ExportBar(exportData, "pages-ai-visibility")}
      </div>
      ${Card(`
        <table style="width:100%;border-collapse:collapse">
          <thead><tr>${TH("Page")}${TH("Status")}${TH("Google Search")}${TH("AI visibility")}${TH("Impressions", "text-align:right")}${TH("Clicks", "text-align:right")}${TH("AI impr.", "text-align:right")}${R.meta.hasSerp ? TH(AioLabel("AIO clicks", 10), "text-align:right") : ""}${TH("")}</tr></thead>
          <tbody>
            ${rows.map(p => `
              <tr data-select-page="${escapeHtml(p.url)}" class="click-row" style="cursor:pointer">
                ${TD(`<span style="display:inline-flex;align-items:center;max-width:100%"><span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escapeHtml(p.url)}</span>${OpenLink(p.url, 11)}</span>`, { style: `color:${T.violet};font-family:${MONO};font-size:11.5px;max-width:260px` })}
                ${TD(StatusPill(p.status))}
                ${TD(p.searchScore == null ? Pill(T.muted, T.lineSoft, "\u2014") : Pill(SEARCH_LABEL_C[searchLabel(p.searchScore)], SEARCH_LABEL_BG[searchLabel(p.searchScore)], searchLabel(p.searchScore)))}
                ${TD(Pill(T.magenta, T.magentaSoft, p.aiScore == null ? (p.ai.imp > 0 ? "Strong" : "\u2014") : aiLabel(p.aiScore)))}
                ${TD(fmt(p.trad.imp), { mono: true, style: "text-align:right" })}
                ${TD(fmt(p.trad.clicks), { mono: true, style: "text-align:right" })}
                ${TD(fmt(p.ai.imp), { mono: true, style: `text-align:right;color:${T.magenta}` })}
                ${R.meta.hasSerp ? TD(p.aioClicks == null ? "\u2014" : fmt(p.aioClicks), { mono: true, style: `text-align:right;color:${T.magenta}` }) : ""}
                ${TD(`<span style="color:${T.muted}">›</span>`)}
              </tr>`).join("")}
          </tbody>
        </table>
        ${Pager({ page: st.page, total: filtered.length, pageSize: PAGE_SIZE, noun: "pages" })}`,
      { style: "padding:0;overflow:hidden" })}`;

    el.querySelector("#pages-search").addEventListener("input", e => {
      st.search = e.target.value;
      st.page = 0;
      debouncedDraw(true);
    });
    el.querySelectorAll("[data-chip-group='status']").forEach(b => b.addEventListener("click", () => {
      st.status = b.dataset.chipValue;
      st.page = 0;
      draw();
    }));
    wirePager(el, st.page, p => { st.page = p; draw(); });
    wireOpenLinks(el);
    el.querySelectorAll("[data-select-page]").forEach(tr => tr.addEventListener("click", () => setSelected(tr.dataset.selectPage)));
    if (focusSearch) {
      const inp = el.querySelector("#pages-search");
      inp.focus();
      inp.setSelectionRange(inp.value.length, inp.value.length);
    }
  };
  draw();
}
